import { Column, Index } from 'typeorm';
import { AI_USAGE_BUCKETS } from '../../constants/ai-usage';
import type { AiUsageBucket, AiUsageSurface } from '../../constants/ai-usage';
import { Entity } from '../../decorators';
import type { AiUsageUnitsModel } from '../dto/ai-usage.dto';
import { BigIntBaseEntity } from './base.entity';
import { decimalNumber } from './decimal-number.transformer';

/**
 * PD-16760: the AI usage ledger. One row per metered charge (an MCP tool
 * call, an agent turn, a voice call, a background AI job), written through
 * the `ai-usage` queue by AiUsageLedgerService. It is the source of truth
 * for every usage view and for the rollups `ai_usage_user_period` and
 * `ai_usage_tenant_period`, which are rebuilt from it nightly.
 *
 * Append-only: a row is never updated or deleted. A rate change applies only
 * to later events, because each row keeps the credits it was priced at and
 * the rate card version that priced them.
 *
 * Not audited (`auditable: false`): the table is high-volume (one row per
 * tool call or agent turn) and immutable, so an `_audit` row per insert
 * would double the write load and record nothing the row itself does not.
 * The same reasoning as `email_event` and `sensor_reading`.
 *
 * The user columns are plain ids, not relations: the ledger must accept a
 * row for any user the caller names without a foreign-key check, and must
 * keep it if that user is later removed.
 *
 * SM1 exposure: none. SM1 names no such table.
 */
@Entity('ai_usage_event', {}, { auditable: false })
@Index('idx_ai_usage_event_period_user', ['periodKey', 'userId'])
@Index('idx_ai_usage_event_period_surface', ['periodKey', 'surface'])
@Index('idx_ai_usage_event_occurred_at', ['occurredAt'])
export class AiUsageEventEntity extends BigIntBaseEntity {
	@Column('datetime', { name: 'occurredAt', precision: 3 })
	public occurredAt!: Date;

	/** `YYYY-MM`, the calendar month in the tenant's zone (`aiUsagePeriodKey`). */
	@Column('char', { name: 'periodKey', length: 7 })
	public periodKey!: string;

	@Column('varchar', { name: 'surface', length: 24 })
	public surface!: AiUsageSurface;

	/** Tool name, agent type, or background feature (`email_ingest`, ...). */
	@Column('varchar', { name: 'feature', length: 48 })
	public feature!: string;

	/** Tenant-local `user._id`; null for a system consumer. */
	@Column('int', { name: 'fk_user__id', unsigned: true, nullable: true })
	public userId!: number | null;

	/** Global `dvp._user._id`, the key the RBAC and credit caches use. */
	@Column('int', { name: 'workdUserId', unsigned: true, nullable: true })
	public workdUserId!: number | null;

	@Column('int', {
		name: 'fk__conversation_agent__id',
		unsigned: true,
		nullable: true,
	})
	public conversationAgentId!: number | null;

	/** Conversation session, call SID, or MCP request id. */
	@Column('varchar', { name: 'sessionRef', length: 64, nullable: true })
	public sessionRef!: string | null;

	/** OAuth `client_id` of an MCP token, or the MCP `clientInfo.name`. */
	@Column('varchar', { name: 'clientName', length: 64, nullable: true })
	public clientName!: string | null;

	@Column('json', { name: 'units' })
	public units!: AiUsageUnitsModel;

	@Column('decimal', {
		name: 'credits',
		precision: 14,
		scale: 4,
		transformer: decimalNumber,
	})
	public credits!: number;

	/** Provider cost. Internal to Workd; never shown in the tenant's UI. */
	@Column('decimal', {
		name: 'costUsd',
		precision: 12,
		scale: 6,
		default: 0,
		transformer: decimalNumber,
	})
	public costUsd!: number;

	/**
	 * The rate card version that priced `credits`. Null only when no version
	 * was in effect at `occurredAt` (then `units.rateCardMissing` is true and
	 * `credits` is 0).
	 */
	@Column('int', { name: 'rateCardVersion', unsigned: true, nullable: true })
	public rateCardVersion!: number | null;

	@Column('enum', {
		name: 'bucket',
		enum: AI_USAGE_BUCKETS,
		default: 'pool',
	})
	public bucket!: AiUsageBucket;

	/** Idempotency key. The unique index is what makes a retried record a no-op. */
	@Index('uk_ai_usage_event_charge_key', { unique: true })
	@Column('varchar', { name: 'chargeKey', length: 128 })
	public chargeKey!: string;
}
